import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, LogOut, Settings, MessageCircle, Package, Truck, FileText, Star, Palette, Sparkles, Loader2, BarChart3, Wand2, ImagePlus, Crop, Check, X, Shield, Tag, Layers, Globe, PieChart, Images, MessageSquare, Download, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import BulkUploadModal from '@/components/BulkUploadModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [artworks, setArtworks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', year: '', medium: '', dimensions: '',
    category: '', art_type: 'Traditional', featured: false, price: '', for_sale: true, tags: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [enhancingText, setEnhancingText] = useState(false);
  const [enhancingImage, setEnhancingImage] = useState(null);
  const [autoCropping, setAutoCropping] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // { artworkId, before, after, message, type }
  const [applyingImage, setApplyingImage] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  const [seoPinging, setSeoPinging] = useState(false);
  const [seoStatus, setSeoStatus] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPw, setChangingPw] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchArtworks();
    fetchWatermarkSetting();
    fetchSeoStatus();
  }, [navigate]);

  const fetchWatermarkSetting = async () => {
    try {
      const res = await axios.get(`${API}/settings/watermark`);
      setWatermarkEnabled(res.data.watermark_enabled);
    } catch { /* default true */ }
  };

  const toggleWatermark = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const res = await axios.put(`${API}/settings/watermark`, { watermark_enabled: !watermarkEnabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatermarkEnabled(res.data.watermark_enabled);
      toast.success(res.data.message);
    } catch {
      toast.error('Failed to update watermark setting');
    }
  };

  const fetchSeoStatus = async () => {
    try {
      const res = await axios.get(`${API}/seo/ping-status`);
      setSeoStatus(res.data);
    } catch { /* ignore */ }
  };

  const handlePingSearchEngines = async () => {
    setSeoPinging(true);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/seo/ping-search-engines`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Copy sitemap URL to clipboard
      const sitemapUrl = seoStatus?.sitemap_stats?.sitemap_url || 'https://gangadharsom.art/api/sitemap.xml';
      await navigator.clipboard.writeText(sitemapUrl);
      toast.success('Sitemap URL copied! Submit it to Google Search Console & Bing Webmaster Tools.');
      fetchSeoStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSeoPinging(false);
    }
  };

  const fetchArtworks = async () => {
    try {
      const response = await axios.get(`${API}/artworks`);
      setArtworks(response.data);
    } catch (error) {
      console.error('Error fetching artworks:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const handleOpenModal = (artwork = null) => {
    if (artwork) {
      setEditingArtwork(artwork);
      setFormData({
        title: artwork.title, description: artwork.description || '',
        year: artwork.year || '', medium: artwork.medium || '',
        dimensions: artwork.dimensions || '', category: artwork.category || '',
        art_type: artwork.art_type || 'Traditional', featured: artwork.featured || false,
        price: artwork.price || '', for_sale: artwork.for_sale !== false,
        tags: (artwork.tags || []).join(', ')
      });
    } else {
      setEditingArtwork(null);
      setFormData({
        title: '', description: '', year: '', medium: '', dimensions: '',
        category: '', art_type: 'Traditional', featured: false, price: '', for_sale: true, tags: ''
      });
    }
    setImageFile(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => { setIsModalOpen(false); setEditingArtwork(null); setImageFile(null); setDuplicateWarning(null); };

  const handleImageSelect = async (file) => {
    setImageFile(file);
    setDuplicateWarning(null);
    if (!file || editingArtwork) return;
    // Check for duplicates
    setCheckingDuplicate(true);
    const token = localStorage.getItem('admin_token');
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/artworks/check-duplicate`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.duplicate) {
        setDuplicateWarning(res.data);
      }
    } catch { /* ignore */ }
    finally { setCheckingDuplicate(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const token = localStorage.getItem('admin_token');
    const fd = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'tags') {
        const tagsArr = formData[key] ? formData[key].split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean) : [];
        fd.append('tags', JSON.stringify(tagsArr));
      } else if (formData[key] !== null && formData[key] !== '') {
        fd.append(key, formData[key]);
      }
    });
    if (imageFile) fd.append('image', imageFile);
    try {
      if (editingArtwork) {
        await axios.put(`${API}/artworks/${editingArtwork.id}`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Artwork updated');
      } else {
        if (!imageFile) { toast.error('Please select an image'); setIsSubmitting(false); return; }
        await axios.post(`${API}/artworks`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Artwork created');
      }
      handleCloseModal();
      fetchArtworks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save artwork');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (artworkId) => {
    if (!window.confirm('Are you sure you want to delete this artwork?')) return;
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/artworks/${artworkId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Artwork deleted');
      fetchArtworks();
    } catch {
      toast.error('Failed to delete artwork');
    }
  };

  const handleSingleAiAnalyze = async () => {
    if (!imageFile && !editingArtwork) { toast.error('Please select an image first'); return; }
    setIsAnalyzing(true);
    const token = localStorage.getItem('admin_token');
    try {
      let res;
      if (imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        res = await axios.post(`${API}/artworks/analyze-image`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }, timeout: 60000
        });
      } else {
        res = await axios.post(`${API}/artworks/${editingArtwork.id}/analyze`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      if (res.data.success) {
        setFormData(prev => ({
          ...prev,
          title: res.data.title || prev.title,
          description: res.data.description || prev.description,
          medium: res.data.medium || prev.medium,
          category: res.data.category || prev.category,
          tags: (res.data.tags || []).join(', ') || prev.tags
        }));
        toast.success('AI analysis complete');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleEnhanceText = async () => {
    if (!editingArtwork) { toast.error('Save artwork first to enhance text'); return; }
    setEnhancingText(true);
    const token = localStorage.getItem('admin_token');
    try {
      const tagsArr = formData.tags ? formData.tags.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean) : [];
      const res = await axios.post(`${API}/artworks/${editingArtwork.id}/enhance-text`, {
        title: formData.title, description: formData.description, tags: tagsArr
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 });
      if (res.data.success) {
        setFormData(prev => ({
          ...prev,
          title: res.data.title || prev.title,
          description: res.data.description || prev.description,
          tags: (res.data.tags || []).join(', ') || prev.tags
        }));
        toast.success('Text enhanced');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Enhancement failed');
    } finally {
      setEnhancingText(false);
    }
  };

  const handleEnhanceImage = async (artId) => {
    setEnhancingImage(artId);
    const token = localStorage.getItem('admin_token');
    const art = artworks.find(a => a.id === artId);
    try {
      const res = await axios.post(`${API}/artworks/${artId}/enhance-image`, { preview: true }, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 120000
      });
      if (res.data.success && res.data.preview_image) {
        setImagePreview({
          artworkId: artId,
          before: art?.image_url,
          after: res.data.preview_image,
          message: res.data.message,
          type: 'enhance',
          sizes: `${res.data.original_size} → ${res.data.new_size}`,
        });
      } else {
        toast.info(res.data.message || 'No changes');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image enhancement failed');
    } finally {
      setEnhancingImage(null);
    }
  };

  const handleAutoCrop = async (artId) => {
    setAutoCropping(artId);
    const token = localStorage.getItem('admin_token');
    const art = artworks.find(a => a.id === artId);
    try {
      const res = await axios.post(`${API}/artworks/${artId}/auto-crop`, { preview: true }, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 120000
      });
      if (res.data.success && res.data.preview_image) {
        setImagePreview({
          artworkId: artId,
          before: art?.image_url,
          after: res.data.preview_image,
          message: res.data.message,
          type: 'crop',
          sizes: `${res.data.original_size} → ${res.data.new_size}`,
        });
      } else {
        toast.info(res.data.message || 'No crop needed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-crop failed');
    } finally {
      setAutoCropping(null);
    }
  };

  const handleAcceptPreview = async () => {
    if (!imagePreview?.after) return;
    setApplyingImage(true);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/artworks/${imagePreview.artworkId}/apply-image`, {
        image_url: imagePreview.after
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
      toast.success('Changes applied');
      setImagePreview(null);
      fetchArtworks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplyingImage(false);
    }
  };

  const handleRejectPreview = () => {
    setImagePreview(null);
    toast.info('Changes discarded');
  };

  const handleEnrichAll = async () => {
    setEnriching(true);
    const token = localStorage.getItem('admin_token');
    try {
      const dimRes = await axios.post(`${API}/artworks/detect-dimensions`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const dimCount = dimRes.data.updated || 0;
      const allArt = await axios.get(`${API}/artworks`);
      const needsEnrich = allArt.data.filter(a => !a.tags || a.tags.length === 0 || !a.description);
      let enrichCount = 0, enrichFailed = 0;
      for (const art of needsEnrich) {
        try {
          await axios.post(`${API}/artworks/${art.id}/generate-tags`, {}, {
            headers: { Authorization: `Bearer ${token}` }, timeout: 60000
          });
          enrichCount++;
          toast.success(`Enriched "${art.title.substring(0, 30)}..."`);
          if (needsEnrich.indexOf(art) < needsEnrich.length - 1) await new Promise(r => setTimeout(r, 3000));
        } catch {
          enrichFailed++;
          await new Promise(r => setTimeout(r, 5000));
        }
      }
      toast.success(`Done! Dimensions: ${dimCount}, Enriched: ${enrichCount} artworks${enrichFailed > 0 ? `, ${enrichFailed} failed` : ''}`);
      fetchArtworks();
    } catch (e) {
      toast.error('Enrichment failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setEnriching(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="admin-dashboard">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="flex justify-between items-center mb-12">
          <div>
            <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight" data-testid="dashboard-title">Admin Dashboard</h1>
            <p className="font-sans text-muted-foreground mt-2">Manage artworks</p>
          </div>
          <div className="flex gap-4 flex-wrap">
            <Link to="/admin/customize"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="customize-site-button"><Settings size={16} className="mr-2" />Customize Site</Button></Link>
            <Link to="/admin/chatbot"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="manage-chatbot-button"><MessageCircle size={16} className="mr-2" />Manage Chatbot</Button></Link>
            <Link to="/admin/orders"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="manage-orders-button"><Package size={16} className="mr-2" />Manage Orders</Button></Link>
            <Link to="/admin/prodigi"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="pod-config-button"><Truck size={16} className="mr-2" />Prodigi Setup</Button></Link>
            <Link to="/admin/qikink"><Button variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white" data-testid="qikink-config-button"><Package size={16} className="mr-2" />Qikink Setup</Button></Link>
            <Link to="/admin/pages"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="page-content-editor-button"><FileText size={16} className="mr-2" />Edit Pages</Button></Link>
            <Link to="/admin/testimonials"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="testimonial-manager-button"><Star size={16} className="mr-2" />Testimonials</Button></Link>
            <Link to="/admin/reviews"><Button variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white" data-testid="review-manager-button"><MessageSquare size={16} className="mr-2" />Buyer Reviews</Button></Link>
            <Link to="/admin/commissions"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="commission-manager-button"><Palette size={16} className="mr-2" />Commissions</Button></Link>
            <Link to="/admin/analytics"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="analytics-dashboard-button"><BarChart3 size={16} className="mr-2" />Analytics</Button></Link>
            <Link to="/admin/print-analytics"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="print-analytics-button"><PieChart size={16} className="mr-2" />Print Analytics</Button></Link>
            <Link to="/admin/bulk-editor"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="bulk-editor-button"><Images size={16} className="mr-2" />Bulk Editor</Button></Link>
            <Link to="/admin/blog"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="blog-manager-button"><FileText size={16} className="mr-2" />Blog</Button></Link>
            <Link to="/admin/discounts"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="discount-manager-button"><Tag size={16} className="mr-2" />Discounts</Button></Link>
            <Link to="/admin/collections"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="collection-manager-button"><Layers size={16} className="mr-2" />Collections</Button></Link>
            <Link to="/admin/featured"><Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="featured-order-button"><Star size={16} className="mr-2" />Featured Order</Button></Link>
            <Link to="/admin/marketing"><Button variant="outline" className="border-green-500 text-green-600 hover:bg-green-500 hover:text-white" data-testid="marketing-dashboard-button"><Globe size={16} className="mr-2" />Marketing & Growth</Button></Link>
            <Button onClick={handleEnrichAll} variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white" data-testid="enrich-all-button" disabled={enriching}>
              <Sparkles size={16} className="mr-2" />{enriching ? 'Enriching...' : 'AI Enrich All'}
            </Button>
            <Button onClick={() => setIsBulkModalOpen(true)} data-testid="bulk-upload-button" variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white">
              <Plus size={16} className="mr-2" />Bulk Upload
            </Button>
            <Button onClick={async () => {
              try {
                const adminToken = localStorage.getItem('admin_token');
                const res = await axios.get(`${API}/admin/export`, {
                  headers: { Authorization: `Bearer ${adminToken}` },
                  responseType: 'blob',
                });
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = res.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] || `gangadharsom-export-${new Date().toISOString().slice(0,10)}.json`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success('Export downloaded');
              } catch { toast.error('Export failed'); }
            }} variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white" data-testid="export-data-button">
              <Download size={16} className="mr-2" />Export Data
            </Button>
            <Button onClick={() => handleOpenModal()} data-testid="add-artwork-button" className="bg-accent hover:bg-accent/90">
              <Plus size={16} className="mr-2" />Add Single Artwork
            </Button>
            <Button onClick={() => setShowChangePw(!showChangePw)} variant="outline" data-testid="change-password-button">
              <Lock size={16} className="mr-2" />Change Password
            </Button>
            <Button onClick={handleLogout} variant="outline" data-testid="logout-button">
              <LogOut size={16} className="mr-2" />Logout
            </Button>
          </div>

          {showChangePw && (
            <div className="mt-4 p-4 bg-card border border-border rounded-lg max-w-md" data-testid="change-password-form">
              <h3 className="font-sans text-sm font-semibold mb-3">Change Admin Password</h3>
              <div className="space-y-2">
                <input type="password" placeholder="Current password" value={pwForm.current}
                  onChange={e => setPwForm({ ...pwForm, current: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background"
                  data-testid="current-password-input" />
                <input type="password" placeholder="New password (min 12 chars)" value={pwForm.new}
                  onChange={e => setPwForm({ ...pwForm, new: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background"
                  data-testid="new-password-input" />
                <input type="password" placeholder="Confirm new password" value={pwForm.confirm}
                  onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background"
                  data-testid="confirm-password-input" />
                <div className="flex gap-2 pt-1">
                  <Button size="sm" disabled={changingPw} className="bg-accent hover:bg-accent/90"
                    data-testid="save-password-btn"
                    onClick={async () => {
                      if (pwForm.new !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
                      if (pwForm.new.length < 12) { toast.error('Use at least 12 characters'); return; }
                      setChangingPw(true);
                      try {
                        const adminToken = localStorage.getItem('admin_token');
                        await axios.put(`${API}/admin/change-password`,
                          { current_password: pwForm.current, new_password: pwForm.new },
                          { headers: { Authorization: `Bearer ${adminToken}` } });
                        toast.success('Password changed');
                        setShowChangePw(false);
                        setPwForm({ current: '', new: '', confirm: '' });
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
                      finally { setChangingPw(false); }
                    }}>
                    {changingPw ? 'Saving...' : 'Save'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowChangePw(false); setPwForm({ current: '', new: '', confirm: '' }); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Settings Bar */}
        <div className="flex items-center gap-6 mb-8 p-4 bg-card rounded-lg border border-border flex-wrap">
          <div className="flex items-center gap-3">
            <Shield size={16} className={watermarkEnabled ? 'text-green-600' : 'text-muted-foreground'} />
            <span className="font-sans text-sm">Image Watermark</span>
            <button
              onClick={toggleWatermark}
              data-testid="watermark-toggle"
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${watermarkEnabled ? 'bg-accent' : 'bg-muted'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${watermarkEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-xs text-muted-foreground">{watermarkEnabled ? 'Protected' : 'Off'}</span>
          </div>
          <div className="h-6 w-px bg-border" />
          <div className="flex items-center gap-3">
            <Globe size={16} className="text-blue-500" />
            <span className="font-sans text-sm">SEO</span>
            <Button
              size="sm" variant="outline"
              onClick={handlePingSearchEngines}
              disabled={seoPinging}
              data-testid="ping-search-engines-btn"
              className="h-7 text-xs border-blue-200 text-blue-600 hover:bg-blue-50"
            >
              {seoPinging ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Globe size={12} className="mr-1" />}
              Copy Sitemap URL
            </Button>
            <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer"
              className="text-[10px] text-blue-500 hover:underline">Google Console</a>
            <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer"
              className="text-[10px] text-blue-500 hover:underline">Bing Tools</a>
            {seoStatus?.sitemap_stats && (
              <span className="text-[10px] text-muted-foreground">
                {seoStatus.sitemap_stats.artworks} artworks, {seoStatus.sitemap_stats.blog_posts} posts, {seoStatus.sitemap_stats.collections} collections in sitemap
              </span>
            )}
          </div>
        </div>

        {/* Artworks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {artworks.map((artwork) => (
            <motion.div key={artwork.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-lg overflow-hidden" data-testid={`artwork-item-${artwork.id}`}>
              <div className="artwork-frame aspect-square relative flex items-center justify-center">
                <img src={artwork.image_url} alt={artwork.title} className="max-w-full max-h-full object-contain" />
                {artwork.featured && (
                  <div className="absolute top-4 right-4 bg-accent text-white px-3 py-1 rounded-full text-xs font-sans uppercase tracking-wider">Featured</div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-serif text-xl font-light mb-1">{artwork.title}</h3>
                <p className="font-sans text-sm text-muted-foreground">
                  {artwork.year} {artwork.category ? `· ${artwork.category}` : ''} · {artwork.art_type}
                </p>
                <div className="flex items-center gap-2 mt-1 mb-3">
                  <span className="font-serif text-lg" data-testid={`artwork-price-display-${artwork.id}`}>
                    {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(artwork.price || 0)}
                  </span>
                  {artwork.for_sale === false && (
                    <span className="text-[10px] font-sans uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Sold</span>
                  )}
                </div>
                {artwork.tags && artwork.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3" data-testid={`artwork-tags-${artwork.id}`}>
                    {artwork.tags.slice(0, 6).map((tag, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 bg-accent/10 text-accent rounded-full font-sans">#{tag}</span>
                    ))}
                    {artwork.tags.length > 6 && <span className="text-[10px] text-muted-foreground">+{artwork.tags.length - 6} more</span>}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleOpenModal(artwork)} data-testid={`edit-artwork-${artwork.id}`}>
                    <Edit size={14} className="mr-1" />Quick Edit
                  </Button>
                  <Button size="sm" variant="outline" className="border-violet-200 text-violet-700 hover:bg-violet-50"
                    onClick={() => navigate(`/admin/artwork/${artwork.id}`)} data-testid={`open-editor-${artwork.id}`}>
                    <Wand2 size={14} className="mr-1" />AI Editor
                  </Button>
                  <Button size="sm" variant="outline" className="border-sky-200 text-sky-700 hover:bg-sky-50"
                    onClick={() => handleAutoCrop(artwork.id)} disabled={autoCropping === artwork.id} data-testid={`auto-crop-${artwork.id}`}>
                    {autoCropping === artwork.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Crop size={14} className="mr-1" />}
                    {autoCropping === artwork.id ? 'Cropping...' : 'Auto-Crop'}
                  </Button>
                  <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => handleEnhanceImage(artwork.id)} disabled={enhancingImage === artwork.id} data-testid={`enhance-image-${artwork.id}`}>
                    {enhancingImage === artwork.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ImagePlus size={14} className="mr-1" />}
                    {enhancingImage === artwork.id ? 'Enhancing...' : 'Enhance'}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(artwork.id)} data-testid={`delete-artwork-${artwork.id}`}>
                    <Trash2 size={14} className="mr-1" />Delete
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {artworks.length === 0 && (
          <div className="text-center py-24" data-testid="no-artworks">
            <p className="font-sans text-muted-foreground mb-4">No artworks yet. Add your first artwork!</p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => setIsBulkModalOpen(true)} variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white">
                <Plus size={16} className="mr-2" />Bulk Upload
              </Button>
              <Button onClick={() => handleOpenModal()} className="bg-accent hover:bg-accent/90">
                <Plus size={16} className="mr-2" />Add Single Artwork
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Upload Modal (Extracted Component) */}
      <BulkUploadModal open={isBulkModalOpen} onOpenChange={setIsBulkModalOpen} onComplete={fetchArtworks} />

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="artwork-form-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">
              {editingArtwork ? 'Edit Artwork' : 'Add New Artwork'}
            </DialogTitle>
            <DialogDescription className="sr-only">Artwork form</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required data-testid="artwork-title-input" />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} data-testid="artwork-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="year">Year</Label>
                <Input id="year" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} data-testid="artwork-year-input" />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <select id="category" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} data-testid="artwork-category-input"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg font-sans text-base focus:outline-none focus:ring-2 focus:ring-accent">
                  <option value="">Select Category</option>
                  <option value="Uncategorized">Uncategorized</option>
                  <optgroup label="Art Styles">
                    <option value="Radha Krishna">Radha Krishna</option>
                    <option value="Mythology">Mythology</option>
                    <option value="Devotional">Devotional</option>
                    <option value="Landscape">Landscape</option>
                    <option value="Abstract">Abstract</option>
                    <option value="Contemporary">Contemporary</option>
                    <option value="Still Life">Still Life</option>
                  </optgroup>
                  <optgroup label="Portraits">
                    <option value="Portrait - Person">Portrait - Person</option>
                    <option value="Portrait - Family">Portrait - Family</option>
                    <option value="Portrait - Pet">Portrait - Pet</option>
                  </optgroup>
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="medium">Medium</Label>
              <Input id="medium" value={formData.medium} onChange={(e) => setFormData({ ...formData, medium: e.target.value })} data-testid="artwork-medium-input" />
            </div>
            <div>
              <Label htmlFor="dimensions">Dimensions</Label>
              <Input id="dimensions" value={formData.dimensions} onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })} placeholder="e.g., 8ft x 5ft" data-testid="artwork-dimensions-input" />
            </div>
            <div>
              <Label htmlFor="tags">Tags / Hashtags</Label>
              <Input id="tags" value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} placeholder="krishna, devotional, oil-painting" data-testid="artwork-tags-input" />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated. Auto-generated by AI analysis.</p>
            </div>
            <div>
              <Label htmlFor="art_type">Art Type</Label>
              <select id="art_type" value={formData.art_type} onChange={(e) => setFormData({ ...formData, art_type: e.target.value })} data-testid="artwork-art-type-select"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg font-sans text-base focus:outline-none focus:ring-2 focus:ring-accent">
                <option value="Traditional">Traditional (Oil, Canvas, Watercolor, etc.)</option>
                <option value="Digital">Digital Art</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Price (INR)</Label>
                <Input id="price" type="number" min="0" step="100" value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder={formData.art_type === 'Digital' ? '5000' : '10000'} data-testid="artwork-price-input" />
                <p className="text-xs text-muted-foreground mt-1">Leave blank for default: {formData.art_type === 'Digital' ? '₹5,000' : '₹10,000'}</p>
              </div>
              <div className="flex items-end pb-6">
                <div className="flex items-center space-x-2">
                  <Checkbox id="for_sale" checked={formData.for_sale} onCheckedChange={(checked) => setFormData({ ...formData, for_sale: checked })} data-testid="artwork-for-sale-checkbox" />
                  <Label htmlFor="for_sale" className="cursor-pointer">Available for sale</Label>
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="image">Image {!editingArtwork && '*'}</Label>
              <Input id="image" type="file" accept="image/*" onChange={(e) => handleImageSelect(e.target.files[0])} data-testid="artwork-image-input" />
              {checkingDuplicate && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Checking for duplicates...</p>
              )}
              {duplicateWarning && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg" data-testid="duplicate-warning">
                  <p className="font-sans text-sm font-medium text-yellow-800">Similar painting already uploaded!</p>
                  {duplicateWarning.matches?.map((m, i) => (
                    <div key={i} className="flex items-center gap-3 mt-2">
                      {m.thumb_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${m.thumb_url}`} alt={m.title} className="w-12 h-12 object-cover rounded" />}
                      <div>
                        <p className="font-sans text-xs font-medium text-yellow-900">{m.title}</p>
                        <p className="font-sans text-[10px] text-yellow-700">{m.similarity}% similar{m.category ? ` · ${m.category}` : ''}</p>
                      </div>
                    </div>
                  ))}
                  <p className="font-sans text-[10px] text-yellow-600 mt-2">You can still upload if this is a different version.</p>
                </div>
              )}
              {(imageFile || editingArtwork) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button type="button" onClick={handleSingleAiAnalyze} disabled={isAnalyzing} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="ai-analyze-btn">
                    {isAnalyzing ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analyzing...</> : <><Sparkles size={14} className="mr-2" /> AI Analyze (Auto-fill)</>}
                  </Button>
                  {editingArtwork && (
                    <Button type="button" onClick={handleEnhanceText} disabled={enhancingText} variant="outline" className="border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="ai-enhance-text-btn">
                      {enhancingText ? <><Loader2 size={14} className="mr-2 animate-spin" /> Enhancing...</> : <><Wand2 size={14} className="mr-2" /> AI Enhance Text</>}
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="featured" checked={formData.featured} onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })} data-testid="artwork-featured-checkbox" />
              <Label htmlFor="featured" className="cursor-pointer">Featured artwork (display on homepage)</Label>
            </div>
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={handleCloseModal} data-testid="cancel-button">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-accent hover:bg-accent/90" data-testid="save-artwork-button">
                {isSubmitting ? 'Saving...' : (editingArtwork ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Before/After Preview Modal */}
      <Dialog open={!!imagePreview} onOpenChange={() => setImagePreview(null)}>
        <DialogContent className="max-w-4xl" data-testid="before-after-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">
              {imagePreview?.type === 'crop' ? 'Auto-Crop Preview' : 'Enhancement Preview'}
            </DialogTitle>
            <DialogDescription className="sr-only">Compare before and after image changes</DialogDescription>
          </DialogHeader>
          {imagePreview && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground font-sans">{imagePreview.message}</p>
              <p className="text-xs text-muted-foreground font-sans">{imagePreview.sizes}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-sans uppercase tracking-[0.12em] text-muted-foreground text-center mb-2">Before</p>
                  <div className="aspect-square bg-muted/20 rounded-lg flex items-center justify-center p-2 border border-border">
                    <img src={imagePreview.before} alt="Before" className="max-w-full max-h-full object-contain rounded" data-testid="modal-before-image" />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-sans uppercase tracking-[0.12em] text-accent text-center mb-2">After</p>
                  <div className="aspect-square bg-muted/20 rounded-lg flex items-center justify-center p-2 border border-accent/30">
                    <img src={imagePreview.after} alt="After" className="max-w-full max-h-full object-contain rounded" data-testid="modal-after-image" />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={handleAcceptPreview} disabled={applyingImage} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="modal-accept-btn">
                  {applyingImage ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Applying...</> : <><Check size={14} className="mr-1.5" /> Accept Changes</>}
                </Button>
                <Button onClick={handleRejectPreview} variant="outline" className="flex-1 border-red-200 text-red-700 hover:bg-red-50" data-testid="modal-reject-btn">
                  <X size={14} className="mr-1.5" /> Discard
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
